import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import type { EditForClarityDetection, EditForClarityIntensity } from "./ai/editForClarity";
import type { EditForClarityReviewStatus } from "./hooks/useEditForClarityReviewWorkflow";

export type EditForClaritySettings = {
    intensity: EditForClarityIntensity;
};

type EditForClaritySidebarProps = {
    onBack: () => void;
    onSubmit: (settings: EditForClaritySettings) => void;
    onDone?: () => void;
    onAcceptAllRemaining?: () => void;
    onPreviewClick?: (id: string) => void;
    onAcceptOne?: (id: string) => void;
    onRejectOne?: (id: string) => void;
    isLoading?: boolean;
    isApplying?: boolean;
    hasRun?: boolean;
    error?: string | null;
    draft?: EditForClarityDetection[];
    statusById?: Record<string, EditForClarityReviewStatus>;
    busyById?: Record<string, boolean>;
    activeId?: string | null;
};

const intensityOptions: { value: EditForClarityIntensity; label: string }[] = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "heavy", label: "Heavy" },
];

const formatMMSS = (seconds: number): string => {
    const s = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const truncate = (value: string, maxChars: number): string => {
    const cleaned = String(value ?? "").replace(/\s+/g, " ").trim();
    if (!cleaned) return "";
    if (cleaned.length <= maxChars) return cleaned;
    return `${cleaned.slice(0, Math.max(0, maxChars - 3)).trimEnd()}...`;
};

const pct = (value: number): string => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "0%";
    return `${Math.round(Math.max(0, Math.min(1, n)) * 100)}%`;
};

const categoryLabel = (id: string): string => {
    if (id === "retake") return "Retake";
    if (id === "filler_disfluency") return "Filler";
    if (id === "redundant_repetition") return "Repetition";
    if (id === "off_topic_tangent") return "Off-topic";
    if (id === "weak_hedging") return "Hedging";
    return "Clarity";
};

const EditForClaritySidebar: React.FC<EditForClaritySidebarProps> = ({
    onBack,
    onSubmit,
    onDone,
    onAcceptAllRemaining,
    onPreviewClick,
    onAcceptOne,
    onRejectOne,
    isLoading,
    isApplying,
    hasRun,
    error,
    draft,
    statusById,
    busyById,
    activeId,
}) => {
    const [intensity, setIntensity] = useState<EditForClarityIntensity>("medium");
    const hasDraft = Array.isArray(draft) && draft.length > 0;
    const isBusyGlobal = Boolean(isLoading || isApplying);
    const detectButtonLabel = "Submit";
    const selectedIndex = intensityOptions.findIndex((o) => o.value === intensity);

    const rows = useMemo(() => {
        if (!hasDraft) return [];
        return (draft ?? []).map((d, idx) => {
            const id = String(d.id ?? "").trim() || `clarity_${idx}`;
            return {
                key: `${idx}-${id}`,
                id,
                status: statusById?.[id] ?? "pending",
                isBusy: Boolean(busyById?.[id]),
                isActive: String(activeId ?? "") === id,
                start: formatMMSS(Number(d.startSec ?? 0)),
                end: formatMMSS(Number(d.endSec ?? 0)),
                confidence: pct(Number(d.confidence ?? 0)),
                category: categoryLabel(String(d.categoryId ?? "")),
                matchedText: truncate(String(d.matchedText ?? ""), 140),
                reason: truncate(String(d.reason ?? ""), 120),
            };
        });
    }, [activeId, busyById, draft, hasDraft, statusById]);

    const pendingCount = rows.filter((row) => row.status === "pending").length;
    const acceptedCount = rows.filter((row) => row.status === "accepted").length;
    const rejectedCount = rows.filter((row) => row.status === "rejected").length;

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                p: "3px",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginLeft: "8px" }}>
                <IconButton size="small" onClick={onBack} sx={{ p: 0.5 }}>
                    <ArrowBackRoundedIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "var(--app-text-primary)" }}>
                    Edit for clarity
                </Typography>
            </Box>

            <Box
                sx={{
                    backgroundColor: "var(--app-bg-surface)",
                    border: "1px solid var(--app-border-subtle)",
                    borderRadius: "12px",
                    padding: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                }}
            >
                <Typography sx={{ fontSize: 13, color: "var(--app-text-muted)", lineHeight: 1.5 }}>
                    Remove filler words, digressions, blather - all the obvious cuts.
                </Typography>

                <Box
                    sx={{
                        backgroundColor: "var(--app-bg-surface)",
                        border: "1px solid var(--app-border-subtle)",
                        borderRadius: "10px",
                        padding: "12px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                    }}
                >
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "var(--app-text-primary)" }}>
                        Edit intensity
                    </Typography>

                    <Box sx={{ px: "4px", py: "6px" }}>
                        <Box
                            sx={{
                                position: "relative",
                                height: "1.5px",
                                backgroundColor: "var(--app-border-muted)",
                                borderRadius: "1px",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    height: "100%",
                                    width: `${selectedIndex * 50}%`,
                                    backgroundColor: "var(--app-text-primary)",
                                    borderRadius: "1px",
                                    transition: "width 0.15s ease",
                                }}
                            />
                            {intensityOptions.map((opt, idx) => (
                                <Box
                                    key={opt.value}
                                    onClick={() => setIntensity(opt.value)}
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: `${idx * 50}%`,
                                        transform: "translate(-50%, -50%)",
                                        width: idx === selectedIndex ? 11 : 6,
                                        height: idx === selectedIndex ? 11 : 6,
                                        borderRadius: "50%",
                                        backgroundColor: idx <= selectedIndex ? "var(--app-text-primary)" : "var(--app-border-muted)",
                                        cursor: "pointer",
                                        transition: "all 0.15s ease",
                                        zIndex: 1,
                                        "&:hover": {
                                            transform: "translate(-50%, -50%) scale(1.15)",
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: "2px" }}>
                        {intensityOptions.map((opt) => (
                            <Typography
                                key={opt.value}
                                onClick={() => setIntensity(opt.value)}
                                sx={{
                                    fontSize: 13,
                                    color: opt.value === intensity ? "var(--app-text-primary)" : "var(--app-text-subtle)",
                                    fontWeight: opt.value === intensity ? 600 : 400,
                                    cursor: "pointer",
                                    userSelect: "none",
                                    transition: "color 0.15s ease",
                                }}
                            >
                                {opt.label}
                            </Typography>
                        ))}
                    </Box>
                </Box>

                <Button
                    data-tour="clarity-submit"
                    fullWidth
                    onClick={() => onSubmit({ intensity })}
                    disabled={isBusyGlobal}
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 500,
                        textTransform: "none",
                        color: "var(--app-bg-surface)",
                        backgroundColor: "var(--app-text-primary)",
                        borderRadius: "8px",
                        height: "40px",
                        "&:hover": {
                            backgroundColor: "#2B2B2B",
                        },
                        "&:disabled": {
                            backgroundColor: "var(--app-border-subtle)",
                            color: "var(--app-text-subtle)",
                        },
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                        {isBusyGlobal ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : null}
                        <Box>{detectButtonLabel}</Box>
                    </Box>
                </Button>

                {!!error && (
                    <Box
                        sx={{
                            backgroundColor: "#FEF2F2",
                            border: "1px solid #FECACA",
                            borderRadius: "10px",
                            px: "10px",
                            py: "8px",
                        }}
                    >
                        <Typography sx={{ fontSize: 12.5, color: "#991B1B" }}>{error}</Typography>
                    </Box>
                )}

                {!error && !isBusyGlobal && hasRun && !hasDraft ? (
                    <Box
                        sx={{
                            backgroundColor: "var(--app-bg-subtle)",
                            border: "1px solid var(--app-border-subtle)",
                            borderRadius: "10px",
                            px: "10px",
                            py: "8px",
                        }}
                    >
                        <Typography sx={{ fontSize: 12.5, color: "var(--app-text-primary)" }}>
                            No obvious clarity edits detected for this intensity.
                        </Typography>
                    </Box>
                ) : null}
            </Box>

            {hasDraft ? (
                <Box
                    data-tour="clarity-preview-panel"
                    sx={{
                        backgroundColor: "var(--app-bg-surface)",
                        border: "1px solid var(--app-border-subtle)",
                        borderRadius: "12px",
                        padding: "12px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "var(--app-text-primary)" }}>Preview</Typography>
                        <Typography sx={{ fontSize: 12.5, color: "var(--app-text-muted)" }}>
                            {rows.length} {rows.length === 1 ? "edit" : "edits"} · {acceptedCount} accepted · {rejectedCount} omitted
                        </Typography>
                    </Box>
                    <Divider sx={{ borderColor: "var(--app-border-subtle)" }} />

                    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", overflow: "auto" }}>
                        {rows.map((row) => (
                            <Box
                                key={row.key}
                                data-clarity-status={row.status}
                                onClick={() => onPreviewClick?.(row.id)}
                                sx={{
                                    border:
                                        row.status === "accepted"
                                            ? "1px solid #86EFAC"
                                            : row.status === "rejected"
                                                ? "1px solid #FECACA"
                                                : row.isActive
                                                    ? "1.5px solid #60A5FA"
                                                    : "1px solid var(--app-border-subtle)",
                                    backgroundColor:
                                        row.status === "accepted"
                                            ? "rgba(34, 197, 94, 0.08)"
                                            : row.status === "rejected"
                                                ? "rgba(239, 68, 68, 0.08)"
                                                : row.isActive
                                                    ? "var(--app-bg-surface)"
                                                    : "var(--app-bg-surface)",
                                    borderRadius: "10px",
                                    px: "10px",
                                    py: "9px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "6px",
                                    cursor: "pointer",
                                    transition: "all 0.12s ease",
                                    "&:hover": {
                                        borderColor: row.status === "accepted" ? "#4ADE80" : row.status === "rejected" ? "#FCA5A5" : "#CBD5E1",
                                    },
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 12.5,
                                            color: "var(--app-text-muted)",
                                            flexShrink: 0,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {row.start} - {row.end}
                                    </Typography>
                                    <Box
                                        sx={{
                                            px: "7px",
                                            py: "1px",
                                            borderRadius: "999px",
                                            fontSize: 11.5,
                                            fontWeight: 600,
                                            color: "#065F46",
                                            backgroundColor: "rgba(16, 185, 129, 0.12)",
                                            border: "1px solid rgba(16, 185, 129, 0.3)",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {row.category}
                                    </Box>
                                    {/* Confidence badge hidden — not useful for end users */}
                                </Box>
                                <Typography sx={{ fontSize: 12.5, color: "var(--app-text-primary)", lineHeight: 1.45 }}>
                                    {row.matchedText || "(no text)"}
                                </Typography>
                                {row.reason ? (
                                    <Typography sx={{ fontSize: 12, color: "var(--app-text-muted)", lineHeight: 1.4 }}>
                                        {row.reason}
                                    </Typography>
                                ) : null}

                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onAcceptOne?.(row.id);
                                        }}
                                        disabled={isBusyGlobal || row.isBusy || !onAcceptOne}
                                        sx={{
                                            p: 0.45,
                                            border: "1px solid",
                                            borderColor: row.status === "accepted" ? "#16A34A" : "var(--app-border-muted)",
                                            color: row.status === "accepted" ? "#16A34A" : "var(--app-text-muted)",
                                            backgroundColor: row.status === "accepted" ? "#DCFCE7" : "var(--app-bg-surface)",
                                            borderRadius: "6px",
                                            "&:hover": { backgroundColor: row.status === "accepted" ? "#BBF7D0" : "var(--app-bg-subtle)" },
                                        }}
                                    >
                                        {row.isBusy ? <CircularProgress size={13} sx={{ color: "inherit" }} /> : <CheckRoundedIcon sx={{ fontSize: 14 }} />}
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRejectOne?.(row.id);
                                        }}
                                        disabled={isBusyGlobal || row.isBusy || !onRejectOne}
                                        sx={{
                                            p: 0.45,
                                            border: "1px solid",
                                            borderColor: row.status === "rejected" ? "#DC2626" : "var(--app-border-muted)",
                                            color: row.status === "rejected" ? "#DC2626" : "var(--app-text-muted)",
                                            backgroundColor: row.status === "rejected" ? "#FEE2E2" : "var(--app-bg-surface)",
                                            borderRadius: "6px",
                                            "&:hover": { backgroundColor: row.status === "rejected" ? "#FECACA" : "var(--app-bg-subtle)" },
                                        }}
                                    >
                                        <CloseRoundedIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ mt: "2px", display: "flex", alignItems: "center", gap: 1 }}>
                        <Button
                            onClick={onDone}
                            disabled={isBusyGlobal || !onDone}
                            sx={{
                                flex: "0 0 auto",
                                fontFamily: "Inter",
                                fontSize: "13px",
                                fontWeight: 500,
                                textTransform: "none",
                                color: "var(--app-text-primary)",
                                backgroundColor: "transparent",
                                borderRadius: "8px",
                                minHeight: "36px",
                                px: "10px",
                                "&:hover": { backgroundColor: "var(--app-bg-subtle)" },
                            }}
                        >
                            Done
                        </Button>
                        <Button
                            data-tour="clarity-accept-all"
                            fullWidth
                            onClick={onAcceptAllRemaining}
                            disabled={isBusyGlobal || pendingCount <= 0 || !onAcceptAllRemaining}
                            sx={{
                                fontFamily: "Inter",
                                fontSize: "13px",
                                fontWeight: 500,
                                textTransform: "none",
                                color: "var(--app-text-primary)",
                                border: "1px solid var(--app-border-muted)",
                                backgroundColor: "var(--app-bg-surface)",
                                borderRadius: "8px",
                                height: "36px",
                                "&:hover": {
                                    borderColor: "var(--app-text-subtle)",
                                    backgroundColor: "var(--app-bg-subtle)",
                                },
                                "&:disabled": {
                                    borderColor: "var(--app-border-subtle)",
                                    color: "var(--app-text-subtle)",
                                },
                            }}
                        >
                            Accept all above edits {pendingCount > 0 ? `(${pendingCount})` : ""}
                        </Button>
                    </Box>
                </Box>
            ) : null}
        </Box>
    );
};

export default EditForClaritySidebar;
